import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, message, Popover } from 'antd';
import { fetchTaskAiSummary, getDownloadUrl } from '../../api/projectManagementApi';
import { useTaskDrawer } from '../TaskDrawerContext';
import Avatar from './Avatar';
import { getFullName } from '../utils/personUtils';

/**
 * Color and label mapping for file relationships.
 */
function getFileBadgeStyle(relationship = 'Uploaded') {
    switch (relationship) {
        case 'Deliverable':
            return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
        case 'Reviewed':
            return 'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200 dark:border-purple-800';
        case 'Related Reference':
            return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800';
        case 'Mentioned':
            return 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800';
        case 'Uploaded':
        default:
            return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700';
    }
}

/**
 * Visual styling and metadata for review sign-off status.
 */
function getReviewStatusBadge(status = 'Not Submitted') {
    switch (status) {
        case 'Approved':
            return {
                bg: 'bg-emerald-50 dark:bg-emerald-950/60',
                text: 'text-emerald-700 dark:text-emerald-300',
                border: 'border-emerald-200 dark:border-emerald-800',
                icon: 'verified',
                label: 'Approved'
            };
        case 'Changes Requested':
            return {
                bg: 'bg-rose-50 dark:bg-rose-950/60',
                text: 'text-rose-700 dark:text-rose-300',
                border: 'border-rose-200 dark:border-rose-800',
                icon: 'cancel',
                label: 'Changes Requested'
            };
        case 'Review Pending':
            return {
                bg: 'bg-amber-50 dark:bg-amber-950/60',
                text: 'text-amber-700 dark:text-amber-300',
                border: 'border-amber-200 dark:border-amber-800',
                icon: 'schedule',
                label: 'Review Pending'
            };
        case 'Not Submitted':
        default:
            return {
                bg: 'bg-slate-100 dark:bg-slate-800',
                text: 'text-slate-600 dark:text-slate-400',
                border: 'border-slate-200 dark:border-slate-700',
                icon: 'hourglass_empty',
                label: 'Not Submitted'
            };
    }
}

/**
 * Interactive person mention hover profile card
 */
function PersonMentionHoverCard({ name, person }) {
    if (!person) {
        return (
            <span className="font-semibold text-slate-800 dark:text-slate-100">
                {name}
            </span>
        );
    }

    const popoverContent = (
        <div className="w-64 p-1 space-y-2.5 font-body">
            <div className="flex items-center gap-2.5">
                <Avatar
                    name={getFullName(person)}
                    photo={person.avatar}
                    size="default"
                    className="w-10 h-10 ring-2 ring-blue-500/20 shadow-xs"
                />
                <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                        {getFullName(person)}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                        {person.department ? `${person.department} · ` : ''}{person.role || person.adminRole || 'Team Member'}
                    </p>
                </div>
            </div>
            <div className="pt-2 space-y-1 text-[11px] text-slate-600 dark:text-slate-300">
                {person.email && (
                    <div className="flex items-center gap-1.5 truncate">
                        <span className="material-symbols-outlined text-[14px] text-slate-400">mail</span>
                        <span className="truncate">{person.email}</span>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Popover content={popoverContent} trigger="hover" placement="top">
            <span className="font-bold text-slate-800 dark:text-slate-100 hover:text-[#03488a] dark:hover:text-[#a7c8ff] cursor-pointer underline decoration-dotted decoration-blue-400 underline-offset-2 transition-colors">
                {name}
            </span>
        </Popover>
    );
}

/**
 * Formats text with person mentions and deep-link anchor highlights
 */
function FormattedModalText({ text, anchor, allCandidates = [], onJumpComment, onJumpAttachment }) {
    if (!text) return null;

    const wrapPersons = (fragment, keyPrefix) => {
        if (!fragment) return null;
        const found = allCandidates.filter((p) => p?.name && fragment.includes(p.name));
        if (found.length === 0) return fragment;

        const namesPattern = found.map((p) => p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
        const re = new RegExp(`\\b(${namesPattern})\\b`, 'g');
        const elements = [];
        let lIndex = 0;
        let m;
        while ((m = re.exec(fragment)) !== null) {
            if (m.index > lIndex) {
                elements.push(fragment.substring(lIndex, m.index));
            }
            const matchedName = m[1];
            const personObj = found.find((p) => p.name === matchedName);
            elements.push(
                <PersonMentionHoverCard key={`${keyPrefix}-person-${m.index}`} name={matchedName} person={personObj} />
            );
            lIndex = re.lastIndex;
        }
        if (lIndex < fragment.length) {
            elements.push(fragment.substring(lIndex));
        }
        return elements;
    };

    if (anchor?.id && anchor?.label && text.includes(anchor.label)) {
        const idx = text.indexOf(anchor.label);
        const before = text.substring(0, idx);
        const labelText = anchor.label;
        const after = text.substring(idx + labelText.length);

        return (
            <span>
                {wrapPersons(before, 'pre')}
                <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (anchor.type === 'comment') onJumpComment?.(anchor.id);
                        else if (anchor.type === 'file') onJumpAttachment?.(anchor.id);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            if (anchor.type === 'comment') onJumpComment?.(anchor.id);
                            else if (anchor.type === 'file') onJumpAttachment?.(anchor.id);
                        }
                    }}
                    className="font-bold text-[#03488a] dark:text-[#a7c8ff] hover:underline cursor-pointer inline-flex items-center gap-0.5 group/link"
                    title={anchor.type === 'comment' ? 'Jump to comment' : 'Jump to attachment'}
                >
                    <span>{labelText}</span>
                    <span className="material-symbols-outlined text-[13px] opacity-75 group-hover/link:opacity-100 transition-opacity">
                        arrow_outward
                    </span>
                </span>
                {wrapPersons(after, 'post')}
            </span>
        );
    }

    return <span>{wrapPersons(text, 'raw')}</span>;
}

const CONTRIBUTOR_COLORS = [
    { bg: 'bg-blue-500', text: 'text-blue-700 dark:text-blue-300', badge: 'bg-blue-50 dark:bg-blue-950/60 border-blue-200 dark:border-blue-800' },
    { bg: 'bg-indigo-500', text: 'text-indigo-700 dark:text-indigo-300', badge: 'bg-indigo-50 dark:bg-indigo-950/60 border-indigo-200 dark:border-indigo-800' },
    { bg: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', badge: 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-200 dark:border-emerald-800' },
    { bg: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-300', badge: 'bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800' },
    { bg: 'bg-purple-500', text: 'text-purple-700 dark:text-purple-300', badge: 'bg-purple-50 dark:bg-purple-950/60 border-purple-200 dark:border-purple-800' },
    { bg: 'bg-rose-500', text: 'text-rose-700 dark:text-rose-300', badge: 'bg-rose-50 dark:bg-rose-950/60 border-rose-200 dark:border-rose-800' },
];

const formatMs = (ms) => {
    if (!ms || ms <= 0) return '0m';
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    return `${minutes}m`;
};

/**
 * TaskAiSummaryModal
 * Expanded AI Task Summary dialog triggered by "View Full Summary" from the Task Overview.
 * Displays perspective-aware categorization: Discussion, Decisions, Actions, Files, Blockers, Progress.
 */
export default function TaskAiSummaryModal({ taskId, taskTitle, open, onClose, initialData = null, taskOverride = null, employeesOverride = [] }) {
    // Try to get context, but don't throw if unavailable (e.g., SharedTaskView has no provider)
    let ctxTask = null, ctxJumpToComment = null, ctxJumpToAttachment = null, ctxEmployees = [];
    try {
        const ctx = useTaskDrawer();
        ctxTask = ctx.task;
        ctxJumpToComment = ctx.jumpToComment;
        ctxJumpToAttachment = ctx.jumpToAttachment;
        ctxEmployees = ctx.employees || [];
    } catch {
        // No provider available — use overrides only
    }

    // Allow caller to pass task/employees directly (e.g. SharedTaskView which has no TaskDrawerProvider)
    const task = taskOverride ?? ctxTask;
    const jumpToComment = ctxJumpToComment ?? (() => { });
    const jumpToAttachment = ctxJumpToAttachment ?? (() => { });
    const employees = employeesOverride.length > 0 ? employeesOverride : ctxEmployees;
    const [loading, setLoading] = useState(false);
    const [summaryData, setSummaryData] = useState(() => initialData || null);
    const [summaryHtml, setSummaryHtml] = useState(() => initialData?.html || '');
    const [error, setError] = useState('');
    const [fetchedId, setFetchedId] = useState(() => (initialData ? taskId : null));
    const [activeSection, setActiveSection] = useState('all'); // 'all' | 'discussion' | 'activity' | 'files' | 'actions'
    const modalRef = useRef(null);

    // Build candidates map for matching mentioned people to hover profile cards
    const allCandidates = useMemo(() => {
        const map = new Map();

        const extractDetails = (p) => {
            if (!p) return null;
            if (typeof p === 'string') {
                const trimmed = p.trim();
                if (!trimmed || trimmed.length < 2 || /^[0-9a-fA-F]{24}$/.test(trimmed)) return null;
                return {
                    _id: null,
                    name: trimmed,
                    email: '',
                    role: 'Team Member',
                    department: '',
                    avatar: '',
                };
            }
            if (typeof p !== 'object') return null;

            let rawName = '';
            if (typeof p.name === 'string') {
                rawName = p.name.trim();
            } else if (p.name && typeof p.name === 'object') {
                rawName = extractDetails(p.name)?.name || '';
            }

            const surname = typeof p.surname === 'string' ? p.surname.trim() : '';
            const firstName = typeof p.firstName === 'string' ? p.firstName.trim() : '';
            const lastName = typeof p.lastName === 'string' ? p.lastName.trim() : '';

            let combinedName = rawName;
            if (!combinedName) {
                combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();
            }
            if (surname && !combinedName.includes(surname)) {
                combinedName = [combinedName, surname].filter(Boolean).join(' ').trim();
            }
            if (!combinedName && typeof p.email === 'string') {
                combinedName = p.email.split('@')[0];
            }
            if (!combinedName || combinedName.length < 2) return null;

            const id = p._id || p.id || null;
            const role = (typeof p.role === 'object' ? p.role?.name : p.role) || p.jobTitle || p.adminRole || 'Team Member';
            const department = (typeof p.department === 'object' ? p.department?.name : p.department) || '';
            const email = typeof p.email === 'string' ? p.email : '';
            const avatar = typeof p.avatar === 'string' ? p.avatar : (typeof p.photo === 'string' ? p.photo : '');

            return {
                _id: id ? String(id) : null,
                name: combinedName,
                email,
                role: typeof role === 'string' ? role : 'Team Member',
                department: typeof department === 'string' ? department : '',
                avatar,
            };
        };

        const addPerson = (raw) => {
            const person = extractDetails(raw);
            if (!person || !person.name) return;
            const lower = person.name.toLowerCase();
            if (!map.has(lower)) {
                map.set(lower, person);
            }
            const parts = person.name.split(' ');
            if (parts.length > 1 && parts[0].length >= 3) {
                const firstName = parts[0];
                const fLower = firstName.toLowerCase();
                if (!map.has(fLower)) {
                    map.set(fLower, { ...person, name: firstName });
                }
            }
        };

        (employees || []).forEach(addPerson);
        if (task?.assignee) addPerson(task.assignee);
        (task?.collaborators || []).forEach(addPerson);
        (task?.reviewers || []).forEach(addPerson);
        (task?.comments || []).forEach((c) => {
            if (!c) return;
            if (c.user) addPerson(c.user);
            if (c.author) addPerson(c.author);
        });
        (summaryData?.mentionedPeople || []).forEach((mp) => {
            if (!mp) return;
            if (typeof mp === 'string') addPerson(mp);
            else if (mp.name) addPerson(mp);
        });

        return Array.from(map.values());
    }, [employees, task, summaryData]);

    const handleFetch = useCallback((force = false) => {
        if (!taskId) return;
        setLoading(true);
        if (force) {
            setSummaryData(null);
            setSummaryHtml('');
        }
        setError('');

        fetchTaskAiSummary(taskId, force)
            .then((res) => {
                if (res.data?.summary) {
                    setSummaryData(res.data.summary);
                }
                setSummaryHtml(res.data?.result || '');
                setFetchedId(taskId);
            })
            .catch((err) => {
                setError(err?.response?.data?.message || 'Failed to generate expanded summary.');
            })
            .finally(() => setLoading(false));
    }, [taskId]);

    useEffect(() => {
        if (initialData) {
            setSummaryData(initialData);
            setFetchedId(taskId);
            setLoading(false);
        }
    }, [initialData, taskId]);

    useEffect(() => {
        if (!open || !taskId) return;
        if (summaryData?.expanded || (initialData?.expanded && fetchedId === taskId)) return;
        if (fetchedId === taskId) return;
        handleFetch(false);
    }, [open, taskId, fetchedId, summaryData, initialData, handleFetch]);

    useEffect(() => {
        if (taskId !== fetchedId && !initialData) {
            setSummaryData(null);
            setSummaryHtml('');
        }
    }, [taskId, fetchedId, initialData]);

    // Close on Escape
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    // Close on backdrop click
    const handleBackdropClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };

    // Open file action
    const handleOpenFile = async (file) => {
        if (!file?.fileId && !taskId) {
            message.info(`File: ${file.name}`);
            return;
        }
        try {
            if (file.fileId) {
                const { data } = await getDownloadUrl(taskId, file.fileId, 'inline');
                if (data?.result?.downloadUrl) {
                    window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
                    return;
                }
            }
            message.info(`Document: ${file.name}`);
        } catch {
            message.warning(`Could not open ${file.name}`);
        }
    };

    // Copy summary to clipboard
    const handleCopy = () => {
        if (!summaryData) return;
        const bulletsText = (summaryData.compactBullets || []).map((b) => `• ${b.text}`).join('\n');
        const overviewText = summaryData.expanded?.overview ? `Overview:\n${summaryData.expanded.overview}\n\n` : '';
        const decisionsText = summaryData.expanded?.decisions?.length
            ? `Decisions:\n${summaryData.expanded.decisions.map((d) => `• ${d}`).join('\n')}\n\n`
            : '';
        const actionsText = summaryData.expanded?.actions?.length
            ? `Actions:\n${summaryData.expanded.actions.map((a) => `• ${a.owner}: ${a.action}`).join('\n')}\n\n`
            : '';
        const textToCopy = `${taskTitle}\n${overviewText}Highlights:\n${bulletsText}\n\n${decisionsText}${actionsText}`.trim();

        navigator.clipboard.writeText(textToCopy);
        message.success('AI Task Summary copied to clipboard!');
    };

    if (!open) return null;

    const expanded = summaryData?.expanded || {};
    const viewerPerspective = summaryData?.viewerPerspective || 'You';

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5"
            style={{ background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(5px)' }}
            onClick={handleBackdropClick}
        >
            <div
                ref={modalRef}
                className="relative w-full max-w-6xl max-h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-[#121824] border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden font-body"
            >
                {/* ── Header ──────────────────────────────────────────────── */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 shrink-0 bg-slate-50/70 dark:bg-[#161f30]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-600 via-indigo-600 to-blue-600 flex items-center justify-center text-white shadow-sm">
                            <span className="material-symbols-outlined text-[22px]">auto_awesome</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-base font-extrabold text-slate-900 dark:text-white font-headline tracking-tight">
                                    Expanded AI Task Summary
                                </h2>

                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-2xl">
                                {taskTitle || 'Task Details'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                        {summaryData && (
                            <Tooltip title="Copy Briefing">
                                <button
                                    onClick={handleCopy}
                                    className="p-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-lg">content_copy</span>
                                </button>
                            </Tooltip>
                        )}
                        <Tooltip title="Regenerate Analysis">
                            <button
                                onClick={() => handleFetch(true)}
                                disabled={loading}
                                className="p-2 text-slate-500 hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-300 rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50"
                            >
                                <span className={`material-symbols-outlined text-lg ${loading ? 'animate-spin' : ''}`}>
                                    refresh
                                </span>
                            </button>
                        </Tooltip>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors cursor-pointer ml-1"
                        >
                            <span className="material-symbols-outlined text-xl">close</span>
                        </button>
                    </div>
                </div>

                {/* ── Section Filter Pills ─────────────────────────────────── */}
                <div className="flex items-center gap-2 px-6 py-2.5 border-b border-slate-100 dark:border-slate-800/80 bg-white dark:bg-[#121824] overflow-x-auto text-xs shrink-0 custom-scrollbar">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">
                        View:
                    </span>
                    {[
                        { key: 'all', label: 'All Sections', icon: 'dashboard' },
                        { key: 'governance', label: 'Team & Reviewers', icon: 'verified_user' },
                        { key: 'blockers', label: 'Blockers & Dependencies', icon: 'warning' },
                        { key: 'actions', label: 'Next Actions', icon: 'bolt' },
                        { key: 'files', label: 'Files & Revisions', icon: 'folder_open' },
                        { key: 'discussion', label: 'Discussions & Decisions', icon: 'forum' },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveSection(tab.key)}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer ${activeSection === tab.key
                                ? 'bg-[#03488a] text-white shadow-xs'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            <span className="material-symbols-outlined text-xs">{tab.icon}</span>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* ── Body ────────────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-50/40 dark:bg-[#121824]">
                    {loading && <LoadingState />}
                    {!loading && error && <ErrorState message={error} onRetry={() => handleFetch(true)} />}

                    {!loading && summaryData && (() => {
                        const teamGov = expanded.teamGovernance || {
                            assignee: task?.assignee ? {
                                name: [task.assignee.name, task.assignee.surname].filter(Boolean).join(' ') || 'Assignee',
                                email: task.assignee.email,
                                role: task?.itemType === 'milestone' ? 'Milestone Owner' : 'Assignee',
                                avatar: task.assignee.photo || task.assignee.avatar,
                                isViewer: false
                            } : null,
                            collaborators: (task?.collaborators || []).map(c => ({
                                name: [c.name, c.surname].filter(Boolean).join(' ') || 'Collaborator',
                                email: c.email,
                                avatar: c.photo || c.avatar,
                                focus: 'Active Contributor'
                            })),
                            reviewers: (task?.reviewers || []).map(r => ({
                                name: [r.name, r.surname].filter(Boolean).join(' ') || 'Reviewer',
                                email: r.email,
                                avatar: r.photo || r.avatar,
                                reviewStatus: task?.status === 'Completed' ? 'Approved' : (task?.status === 'Under Review' ? 'Review Pending' : 'Not Submitted')
                            })),
                            globalReviewStatus: task?.status === 'Completed' ? 'Approved' : (task?.status === 'Under Review' ? 'Review Pending' : 'Not Submitted')
                        };

                        const blockers = expanded.blockers || {
                            active: (expanded.blockersAndDependencies || []).filter(b => b.type?.includes('Active') || (!b.isResolved && !b.type?.includes('Dependency'))).map(b => ({
                                type: 'Active Blocker',
                                reason: b.description || b.reason || 'Task is blocked',
                                timestamp: 'Current'
                            })),
                            resolved: (expanded.blockersAndDependencies || []).filter(b => b.isResolved || b.type?.includes('Resolved')).map(b => ({
                                type: 'Resolved Blocker',
                                description: b.description || 'Blocker cleared',
                                resolvedBy: b.resolvedBy || 'Team Member',
                                resolvedAt: b.resolvedAt || 'Previously'
                            })),
                            dependencies: (task?.dependencies || []).map(d => ({
                                title: d.taskId?.title || 'Upstream Task',
                                status: d.taskId?.status || 'In Progress',
                                isBlocking: d.taskId?.status !== 'Completed'
                            })),
                            hasAny: Boolean((expanded.blockersAndDependencies && expanded.blockersAndDependencies.length > 0) || task?.blocked || task?.status === 'On Hold')
                        };

                        const rawActions = Array.isArray(expanded.actions) ? expanded.actions : [];
                        const seenActionKeys = new Set();
                        const actionsList = rawActions.filter(a => {
                            if (!a || !a.action) return false;
                            const key = a.action.trim().toLowerCase().replace(/['"\s]+/g, ' ');
                            if (seenActionKeys.has(key)) return false;
                            seenActionKeys.add(key);
                            return true;
                        });
                        const yourActions = actionsList.filter(a => a.isViewer);
                        const teamActions = actionsList.filter(a => !a.isViewer);

                        const filesList = Array.isArray(expanded.filesAndDocuments) ? expanded.filesAndDocuments : [];
                        const effortBreakdown = expanded.effortBreakdown || null;

                        return (
                            <div className="space-y-6">
                                {/* Executive Overview Banner */}
                                {(activeSection === 'all' || activeSection === 'discussion') && (
                                    <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50/70 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-200/80 dark:border-blue-900/60 space-y-2">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[#03488a] dark:text-[#a7c8ff] text-lg">
                                                    insights
                                                </span>
                                                <h3 className="text-sm font-bold uppercase tracking-wider text-[#03488a] dark:text-[#a7c8ff]">
                                                    Executive Overview
                                                </h3>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {task?.status && (
                                                    <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-blue-100/70 dark:bg-blue-900/50 text-[#03488a] dark:text-[#a7c8ff]">
                                                        {task.status}
                                                    </span>
                                                )}
                                                {task?.priority && (
                                                    <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                                        {task.priority} Priority
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-sm sm:text-[15px] text-slate-800 dark:text-slate-200 leading-relaxed font-normal">
                                            {expanded.overview || 'Overview of current task state and main work items.'}
                                        </p>
                                    </div>
                                )}

                                {/* ── Team & Review Governance Card ── */}
                                {(activeSection === 'all' || activeSection === 'governance') && (
                                    <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-4">
                                        <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[#03488a] dark:text-[#a7c8ff] text-base">
                                                    verified_user
                                                </span>
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                    Team & Review Governance
                                                </h4>
                                            </div>
                                            {teamGov.globalReviewStatus && (
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[11px] text-slate-400 font-medium">Review Pipeline:</span>
                                                    {(() => {
                                                        const badge = getReviewStatusBadge(teamGov.globalReviewStatus);
                                                        return (
                                                            <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}>
                                                                <span className="material-symbols-outlined text-xs">{badge.icon}</span>
                                                                <span>{badge.label}</span>
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* 1. Task Assignee */}
                                            <div className="p-3.5 rounded-lg border border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-800/30 space-y-2.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                                        {task?.itemType === 'milestone' ? 'Milestone Owner' : 'Assignee'}
                                                    </span>
                                                    {teamGov.assignee?.isViewer && (
                                                        <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 px-1.5 py-0.2 rounded border border-amber-200 dark:border-amber-800">
                                                            Assigned to You
                                                        </span>
                                                    )}
                                                </div>
                                                {teamGov.assignee ? (
                                                    <div className="flex items-center gap-2.5">
                                                        <Avatar
                                                            name={teamGov.assignee.name}
                                                            photo={teamGov.assignee.avatar}
                                                            size="default"
                                                            className="w-9 h-9 ring-2 ring-blue-500/20 shrink-0"
                                                        />
                                                        <div className="min-w-0">
                                                            <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                                                {(() => {
                                                                    const pObj = allCandidates.find(p => (p.name || '').toLowerCase() === teamGov.assignee.name.toLowerCase());
                                                                    return <PersonMentionHoverCard name={teamGov.assignee.name} person={pObj} />;
                                                                })()}
                                                            </p>
                                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                                                                {teamGov.assignee.email || (task?.itemType === 'milestone' ? 'Milestone Owner' : 'Assignee')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic">No assignee assigned.</p>
                                                )}
                                            </div>

                                            {/* 2. Collaborators */}
                                            <div className="p-3.5 rounded-lg border border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-800/30 space-y-2.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                                        Collaborators ({teamGov.collaborators?.length || 0})
                                                    </span>
                                                </div>
                                                {teamGov.collaborators && teamGov.collaborators.length > 0 ? (
                                                    <div className="space-y-2 max-h-36 overflow-y-auto custom-scrollbar pr-1">
                                                        {teamGov.collaborators.map((c, i) => {
                                                            const pObj = allCandidates.find(p => (p.name || '').toLowerCase() === (c.name || '').toLowerCase());
                                                            return (
                                                                <div key={i} className="flex items-center justify-between gap-2">
                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                        <Avatar
                                                                            name={c.name}
                                                                            photo={c.avatar}
                                                                            size="small"
                                                                            className="w-6 h-6 ring-1 ring-slate-300 dark:ring-slate-700 shrink-0"
                                                                        />
                                                                        <div className="min-w-0">
                                                                            <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                                                                                <PersonMentionHoverCard name={c.name} person={pObj} />
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 shrink-0">
                                                                        {c.focus || (c.lastActive ? `Active ${c.lastActive}` : 'Collaborator')}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic">No additional collaborators.</p>
                                                )}
                                            </div>

                                            {/* 3. Reviewers & Approvals */}
                                            <div className="p-3.5 rounded-lg border border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-800/30 space-y-2.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                                        Reviewers & Sign-off ({teamGov.reviewers?.length || 0})
                                                    </span>
                                                </div>
                                                {teamGov.reviewers && teamGov.reviewers.length > 0 ? (
                                                    <div className="space-y-2.5 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                                                        {teamGov.reviewers.map((r, i) => {
                                                            const badge = getReviewStatusBadge(r.reviewStatus);
                                                            const pObj = allCandidates.find(p => (p.name || '').toLowerCase() === (r.name || '').toLowerCase());
                                                            return (
                                                                <div key={i} className="space-y-1.5 p-2 rounded-lg bg-white dark:bg-[#121824] border border-slate-100 dark:border-slate-800">
                                                                    <div className="flex items-center justify-between gap-1.5">
                                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                                            <Avatar
                                                                                name={r.name}
                                                                                photo={r.avatar}
                                                                                size="small"
                                                                                className="w-5 h-5 shrink-0"
                                                                            />
                                                                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                                                                                <PersonMentionHoverCard name={r.name} person={pObj} />
                                                                            </span>
                                                                        </div>
                                                                        <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.2 rounded border ${badge.bg} ${badge.text} ${badge.border}`}>
                                                                            <span className="material-symbols-outlined text-[10px]">{badge.icon}</span>
                                                                            <span>{badge.label}</span>
                                                                        </span>
                                                                    </div>
                                                                    {r.rejectionNote && (
                                                                        <div className="text-[10px] p-1.5 rounded bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 border border-rose-200 dark:border-rose-900">
                                                                            <span className="font-bold">Rejection Note: </span>
                                                                            {r.rejectionNote}
                                                                        </div>
                                                                    )}
                                                                    {r.feedbackText && (
                                                                        <div className="flex items-center justify-between gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                                                                            <span className="truncate italic">"{r.feedbackText}"</span>
                                                                            {r.commentId && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        onClose();
                                                                                        jumpToComment(r.commentId);
                                                                                    }}
                                                                                    className="text-[#03488a] dark:text-[#a7c8ff] hover:underline font-bold shrink-0 cursor-pointer"
                                                                                >
                                                                                    View
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic">No formal reviewers assigned (Review handled by Project Manager or optional).</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ── Team Effort Breakdown Card ── */}
                                {(activeSection === 'all' || activeSection === 'governance') && (
                                    <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-4">
                                        <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[#03488a] dark:text-[#a7c8ff] text-base">
                                                    timer
                                                </span>
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                    Team Effort & Time Breakdown
                                                </h4>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {effortBreakdown && effortBreakdown.contributorsCount > 0 ? (
                                                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/70 text-[#03488a] dark:text-[#a7c8ff] border border-blue-200/80 dark:border-blue-800/60">
                                                        Total Logged: {effortBreakdown.formattedTotalTime} · {effortBreakdown.contributorsCount} Contributor{effortBreakdown.contributorsCount > 1 ? 's' : ''}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                        Total Effort: {formatMs(task?.actualTimeMs || 0)}
                                                    </span>
                                                )}
                                                {task?.estimatedTime && (
                                                    <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                                                        Est: {task.estimatedTime}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {effortBreakdown && Array.isArray(effortBreakdown.contributors) && effortBreakdown.contributors.length > 0 ? (
                                            <div className="space-y-3.5">
                                                {/* Visual Proportion Bar */}
                                                <div className="space-y-1.5">
                                                    <div className="h-2.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex">
                                                        {effortBreakdown.contributors.map((c, i) => {
                                                            const color = CONTRIBUTOR_COLORS[i % CONTRIBUTOR_COLORS.length];
                                                            return (
                                                                <div
                                                                    key={i}
                                                                    style={{ width: `${c.percentage}%` }}
                                                                    className={`${color.bg} h-full transition-all duration-300 relative group`}
                                                                    title={`${c.name}: ${c.formattedTime} (${c.percentage}%)`}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Contributors Grid */}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                    {effortBreakdown.contributors.map((c, i) => {
                                                        const color = CONTRIBUTOR_COLORS[i % CONTRIBUTOR_COLORS.length];
                                                        const pObj = allCandidates.find(p => (p.name || '').toLowerCase() === (c.name || '').toLowerCase());
                                                        return (
                                                            <div
                                                                key={i}
                                                                className="p-3 rounded-lg border border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-800/30 flex items-center justify-between gap-2.5"
                                                            >
                                                                <div className="flex items-center gap-2.5 min-w-0">
                                                                    <Avatar
                                                                        name={c.name}
                                                                        photo={pObj?.avatar || ''}
                                                                        size="default"
                                                                        className="w-8 h-8 shrink-0 ring-1 ring-slate-200 dark:ring-slate-700"
                                                                    />
                                                                    <div className="min-w-0">
                                                                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                                                                            <PersonMentionHoverCard name={c.name} person={pObj} />
                                                                        </p>
                                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                                            {c.activeNow ? (
                                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.2 rounded-full bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                                                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                                                                                    Running Now
                                                                                </span>
                                                                            ) : c.source === 'migrated' ? (
                                                                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/50 px-1.5 py-0.2 rounded border border-purple-200/80 dark:border-purple-800/60">
                                                                                    <span className="material-symbols-outlined text-[10px]">history</span>
                                                                                    {c.sourceLabel || 'Migrated Effort'}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                                                                    CRM Timer · {c.sessionsCount || 1} session{(c.sessionsCount || 1) > 1 ? 's' : ''}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="text-right shrink-0">
                                                                    <p className="text-sm font-black text-slate-900 dark:text-white">
                                                                        {c.formattedTime}
                                                                    </p>
                                                                    <span className={`inline-block text-[10px] font-bold px-1.5 py-0.2 rounded border ${color.badge} ${color.text}`}>
                                                                        {c.percentage}% of total
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="p-3 rounded-lg bg-slate-50/60 dark:bg-slate-800/30 border border-dashed border-slate-200 dark:border-slate-800 text-center">
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    No individual employee timer sessions recorded for this task yet.
                                                    {task?.actualTimeMs ? ` (Total task effort: ${formatMs(task.actualTimeMs)})` : ''}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── Smart Blockers & Dependencies Card (Only Where Necessary) ── */}
                                {((activeSection === 'all' && blockers.hasAny) || activeSection === 'blockers') && (
                                    <div className="space-y-3">
                                        {/* If user navigated specifically to 'blockers' tab but none exist */}
                                        {!blockers.hasAny && activeSection === 'blockers' && (
                                            <div className="p-6 rounded-xl bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 flex items-center gap-3">
                                                <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-2xl">
                                                    task_alt
                                                </span>
                                                <div>
                                                    <h4 className="text-xs font-bold text-emerald-900 dark:text-emerald-200 uppercase tracking-wide">
                                                        No Blockers or Dependencies
                                                    </h4>
                                                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                                                        Workflow is clear. No active impediments or upstream blocking tasks recorded for this work item.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* 🔴 Active Blockers */}
                                        {blockers.active && blockers.active.length > 0 && (
                                            <div className="p-4 rounded-xl bg-rose-50/80 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-900/60 space-y-2.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full bg-rose-600 animate-ping" />
                                                    <span className="material-symbols-outlined text-rose-600 dark:text-rose-400 text-base">
                                                        block
                                                    </span>
                                                    <h3 className="text-xs font-extrabold uppercase tracking-wider text-rose-900 dark:text-rose-200">
                                                        Active Blockers ({blockers.active.length}) — Requires Resolution
                                                    </h3>
                                                </div>
                                                <ul className="space-y-2">
                                                    {blockers.active.map((b, i) => (
                                                        <li key={i} className="p-2.5 rounded-lg bg-white/80 dark:bg-[#161f30] border border-rose-200 dark:border-rose-900/50 flex items-start justify-between gap-3 text-xs text-rose-900 dark:text-rose-200 font-medium">
                                                            <div className="flex items-start gap-2">
                                                                <span className="material-symbols-outlined text-rose-600 text-sm mt-0.5 shrink-0">
                                                                    error
                                                                </span>
                                                                <span>{b.reason || b.description}</span>
                                                            </div>
                                                            <span className="text-[10px] font-bold text-rose-500 dark:text-rose-400 whitespace-nowrap shrink-0">
                                                                {b.timestamp || 'Current'}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* 🟢 Resolved Blockers */}
                                        {blockers.resolved && blockers.resolved.length > 0 && (
                                            <div className="p-4 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-900/50 space-y-2.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-base">
                                                        verified
                                                    </span>
                                                    <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-900 dark:text-emerald-200">
                                                        Resolved Blockers ({blockers.resolved.length}) — Historical Progress
                                                    </h3>
                                                </div>
                                                <ul className="space-y-1.5">
                                                    {blockers.resolved.map((r, i) => (
                                                        <li key={i} className="p-2 rounded-lg bg-white/70 dark:bg-[#161f30] border border-emerald-200/60 dark:border-emerald-900/40 text-xs text-emerald-900 dark:text-emerald-200 flex items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <span className="material-symbols-outlined text-emerald-500 text-xs shrink-0">
                                                                    check_circle
                                                                </span>
                                                                <span className="truncate">{r.description}</span>
                                                            </div>
                                                            <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-medium shrink-0">
                                                                Resolved {r.resolvedAt} {r.resolvedBy ? `by ${r.resolvedBy}` : ''}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* 🔵 Upstream Task Dependencies */}
                                        {blockers.dependencies && blockers.dependencies.length > 0 && (
                                            <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-2.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400 text-base">
                                                        account_tree
                                                    </span>
                                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                        Upstream Task Dependencies ({blockers.dependencies.length})
                                                    </h3>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    {blockers.dependencies.map((d, i) => {
                                                        const isDone = d.status === 'Completed';
                                                        return (
                                                            <div key={i} className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/40 flex items-center justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                                                                        {d.title}
                                                                    </p>
                                                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                                                        Priority: {d.priority || 'Medium'}
                                                                    </p>
                                                                </div>
                                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${isDone
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
                                                                    : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800'
                                                                    }`}>
                                                                    {d.status}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── Actions & Next Steps (Guaranteed) ── */}
                                {(activeSection === 'all' || activeSection === 'actions') && (
                                    <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-4">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-lg">
                                                    bolt
                                                </span>
                                                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                    Action Items & Execution Checklist
                                                </h4>
                                            </div>
                                            <span className="text-xs text-slate-400 font-medium">
                                                Prioritized next steps guaranteed from requirements & reviews
                                            </span>
                                        </div>

                                        {/* 🌟 Your Actions (Highlighted) */}
                                        {yourActions.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800 dark:text-amber-300">
                                                    <span className="material-symbols-outlined text-sm text-amber-600">star</span>
                                                    <span>Your Action Items (Immediate Priority)</span>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {yourActions.map((act, idx) => (
                                                        <div
                                                            key={`your-${idx}`}
                                                            className="p-3.5 rounded-xl border bg-amber-50/80 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800/80 flex items-start gap-2.5 shadow-xs"
                                                        >
                                                            <span className="material-symbols-outlined text-amber-600 text-base shrink-0 mt-0.5">
                                                                task_alt
                                                            </span>
                                                            <div className="space-y-1 min-w-0 flex-1">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="text-[10px] font-extrabold uppercase tracking-wide text-amber-800 dark:text-amber-300 bg-amber-200/60 dark:bg-amber-900/60 px-1.5 py-0.2 rounded">
                                                                        Your Action
                                                                    </span>
                                                                    {act.deadline && (
                                                                        <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400">
                                                                            Due {act.deadline}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-sm text-slate-900 dark:text-slate-100 font-semibold leading-relaxed">
                                                                    <FormattedModalText text={act.action} allCandidates={allCandidates} />
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* 📋 Team Actions */}
                                        {teamActions.length > 0 && (
                                            <div className="space-y-2">
                                                {yourActions.length > 0 && (
                                                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide pt-1">
                                                        Team & Collaborator Follow-ups
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {teamActions.map((act, idx) => {
                                                        const personObj = allCandidates.find(
                                                            p => (p.name || '').toLowerCase() === (act.owner || '').toLowerCase() ||
                                                                (p.name || '').toLowerCase().includes((act.owner || '').toLowerCase())
                                                        );
                                                        return (
                                                            <div
                                                                key={`team-${idx}`}
                                                                className="p-3 rounded-lg border bg-slate-50/80 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700/80 flex items-start gap-2.5"
                                                            >
                                                                <span className="material-symbols-outlined text-slate-400 text-sm mt-0.5 shrink-0">
                                                                    checklist
                                                                </span>
                                                                <div className="space-y-0.5 min-w-0 flex-1">
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                                                                            <PersonMentionHoverCard name={act.owner} person={personObj} />
                                                                        </span>
                                                                        {act.deadline && (
                                                                            <span className="text-[10px] text-slate-400">
                                                                                Due {act.deadline}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed font-medium">
                                                                        <FormattedModalText text={act.action} allCandidates={allCandidates} />
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {actionsList.length === 0 && (
                                            <p className="text-xs text-slate-400 italic">No outstanding actions pending.</p>
                                        )}
                                    </div>
                                )}

                                {/* ── Files & Documents Intelligence (Guaranteed 100% of attached files) ── */}
                                {(activeSection === 'all' || activeSection === 'files') && (
                                    <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-3">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-base">
                                                    folder_open
                                                </span>
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                    Important Files, Revisions & Documents ({filesList.length})
                                                </h4>
                                            </div>
                                            <span className="text-[11px] text-slate-400 font-medium">
                                                Integrated directly with ERP Files & Revision Storage
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                                            {filesList.map((file, idx) => (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleOpenFile(file)}
                                                    className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-700/80 bg-slate-50/60 dark:bg-slate-800/40 hover:bg-blue-50/60 dark:hover:bg-blue-950/40 transition-colors flex items-center justify-between gap-2 cursor-pointer group"
                                                >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="material-symbols-outlined text-slate-500 group-hover:text-[#03488a] dark:group-hover:text-blue-300 text-base shrink-0">
                                                            {file.mimeType?.includes('image') ? 'image' : file.mimeType?.includes('pdf') ? 'picture_as_pdf' : 'description'}
                                                        </span>
                                                        <div className="min-w-0">
                                                            <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate" title={file.name}>
                                                                {file.name}
                                                            </p>
                                                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                                <span className={`text-[10px] px-1.5 py-0.2 rounded border font-semibold ${getFileBadgeStyle(file.relationship)}`}>
                                                                    {file.relationship || 'Uploaded'}
                                                                </span>
                                                                {file.revision && (
                                                                    <span className="text-[10px] text-purple-600 dark:text-purple-400 font-bold bg-purple-50 dark:bg-purple-950/50 px-1 rounded">
                                                                        {file.revision}
                                                                    </span>
                                                                )}
                                                                {file.size && (
                                                                    <span className="text-[10px] text-slate-400">
                                                                        {file.size}
                                                                    </span>
                                                                )}
                                                                {file.timestamp && (
                                                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                                                                        {file.timestamp}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {file.fileId && (
                                                            <Tooltip title="Jump to attachment in Drawer">
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onClose();
                                                                        jumpToAttachment(file.fileId);
                                                                    }}
                                                                    className="p-1 text-slate-400 hover:text-[#03488a] dark:hover:text-blue-300 rounded hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                                                                >
                                                                    <span className="material-symbols-outlined text-xs">open_in_browser</span>
                                                                </button>
                                                            </Tooltip>
                                                        )}
                                                        <span className="material-symbols-outlined text-xs text-slate-400 group-hover:text-[#03488a] dark:group-hover:text-blue-300">
                                                            open_in_new
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                            {filesList.length === 0 && (
                                                <p className="text-xs text-slate-400 italic col-span-full">No active file attachments identified.</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Discussions & Decisions */}
                                {(activeSection === 'all' || activeSection === 'discussion') && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Major Discussion Points */}
                                        <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400 text-lg">
                                                    forum
                                                </span>
                                                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                    Major Discussion Points
                                                </h4>
                                            </div>
                                            <ul className="space-y-2.5">
                                                {(expanded.discussionPoints || []).map((point, idx) => {
                                                    const isObj = typeof point === 'object' && point !== null;
                                                    const text = isObj ? point.text : point;
                                                    const author = isObj ? point.author : null;
                                                    const timestamp = isObj ? point.timestamp : null;
                                                    const anchor = isObj ? point.anchor : null;

                                                    return (
                                                        <li key={idx} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2.5 leading-relaxed">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 shrink-0" />
                                                            <div className="flex-1 min-w-0">
                                                                {author && !text?.toLowerCase().startsWith(author.toLowerCase()) && (
                                                                    <span className="font-bold text-slate-900 dark:text-slate-100 text-sm mr-1.5">
                                                                        {author}:
                                                                    </span>
                                                                )}
                                                                <FormattedModalText
                                                                    text={text}
                                                                    anchor={anchor}
                                                                    allCandidates={allCandidates}
                                                                    onJumpComment={(cid) => {
                                                                        onClose();
                                                                        jumpToComment(cid);
                                                                    }}
                                                                    onJumpAttachment={(fid) => {
                                                                        onClose();
                                                                        jumpToAttachment(fid);
                                                                    }}
                                                                />
                                                                {anchor?.id && (!anchor?.label || !text?.includes(anchor.label)) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            onClose();
                                                                            if (anchor.type === 'comment') jumpToComment(anchor.id);
                                                                            else if (anchor.type === 'file') jumpToAttachment(anchor.id);
                                                                        }}
                                                                        className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0.2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/70 text-[#03488a] dark:text-[#a7c8ff] border border-blue-200 dark:border-blue-800 rounded text-[10px] font-bold cursor-pointer transition-colors align-middle"
                                                                        title={anchor.type === 'comment' ? 'Jump to comment' : 'Jump to file'}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[11px]">link</span>
                                                                        <span>{anchor.type === 'comment' ? 'View Comment' : 'View File'}</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {timestamp && (
                                                                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 whitespace-nowrap shrink-0 mt-0.5">
                                                                    {timestamp}
                                                                </span>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                                {(!expanded.discussionPoints || expanded.discussionPoints.length === 0) && (
                                                    <li className="text-xs text-slate-400 italic">No major discussion threads recorded yet.</li>
                                                )}
                                            </ul>
                                        </div>

                                        {/* Decisions Made */}
                                        <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-lg">
                                                    verified
                                                </span>
                                                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                    Decisions Made
                                                </h4>
                                            </div>
                                            <ul className="space-y-2.5">
                                                {(expanded.decisions || []).map((decision, idx) => {
                                                    const isObj = typeof decision === 'object' && decision !== null;
                                                    const text = isObj ? decision.text : decision;
                                                    const timestamp = isObj ? decision.timestamp : null;
                                                    const anchor = isObj ? decision.anchor : null;

                                                    return (
                                                        <li key={idx} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2.5 leading-relaxed">
                                                            <span className="material-symbols-outlined text-emerald-500 text-xs shrink-0 mt-0.5">
                                                                check_circle
                                                            </span>
                                                            <div className="flex-1 min-w-0">
                                                                <FormattedModalText
                                                                    text={text}
                                                                    anchor={anchor}
                                                                    allCandidates={allCandidates}
                                                                    onJumpComment={(cid) => {
                                                                        onClose();
                                                                        jumpToComment(cid);
                                                                    }}
                                                                    onJumpAttachment={(fid) => {
                                                                        onClose();
                                                                        jumpToAttachment(fid);
                                                                    }}
                                                                />
                                                                {anchor?.id && (!anchor?.label || !text?.includes(anchor.label)) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            onClose();
                                                                            if (anchor.type === 'comment') jumpToComment(anchor.id);
                                                                            else if (anchor.type === 'file') jumpToAttachment(anchor.id);
                                                                        }}
                                                                        className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0.2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/70 text-[#03488a] dark:text-[#a7c8ff] border border-blue-200 dark:border-blue-800 rounded text-[10px] font-bold cursor-pointer transition-colors align-middle"
                                                                        title={anchor.type === 'comment' ? 'Jump to comment' : 'Jump to file'}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[11px]">link</span>
                                                                        <span>{anchor.type === 'comment' ? 'View Comment' : 'View File'}</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {timestamp && (
                                                                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 whitespace-nowrap shrink-0 mt-0.5">
                                                                    {timestamp}
                                                                </span>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                                {(!expanded.decisions || expanded.decisions.length === 0) && (
                                                    <li className="text-xs text-slate-400 italic">No explicit formal decisions logged yet.</li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                {/* Perspective Activity: Your Activity vs Collaborator Activity */}
                                {(activeSection === 'all' || activeSection === 'governance') && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Your Activity */}
                                        <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50/60 to-white dark:from-[#1b1c30] dark:to-[#161f30] border border-purple-200/80 dark:border-purple-900/60 space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-purple-600 dark:text-purple-400 text-base">
                                                    person
                                                </span>
                                                <h4 className="text-sm font-bold uppercase tracking-wider text-purple-900 dark:text-purple-300">
                                                    Recent Audit & Status Activity
                                                </h4>
                                            </div>
                                            <ul className="space-y-2">
                                                {(expanded.yourActivity || []).map((item, idx) => (
                                                    <li key={idx} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2 leading-relaxed">
                                                        <span className="material-symbols-outlined text-purple-500 text-xs shrink-0 mt-0.5">
                                                            arrow_right_alt
                                                        </span>
                                                        <span>
                                                            <FormattedModalText text={item} allCandidates={allCandidates} />
                                                        </span>
                                                    </li>
                                                ))}
                                                {(!expanded.yourActivity || expanded.yourActivity.length === 0) && (
                                                    <li className="text-xs text-slate-400 italic">No recent audit activity recorded.</li>
                                                )}
                                            </ul>
                                        </div>

                                        {/* Collaborator Activity */}
                                        <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-slate-600 dark:text-slate-400 text-base">
                                                    group
                                                </span>
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                    Collaborator Activity
                                                </h4>
                                            </div>
                                            <div className="space-y-3">
                                                {(expanded.collaboratorActivity || []).map((collab, idx) => {
                                                    const personObj = allCandidates.find(
                                                        (p) =>
                                                            (p.name || '').toLowerCase() === (collab.name || '').toLowerCase() ||
                                                            (p.name || '').toLowerCase().includes((collab.name || '').toLowerCase())
                                                    );
                                                    return (
                                                        <div key={idx} className="space-y-1">
                                                            <div className="text-xs font-bold text-slate-800 dark:text-slate-200">
                                                                <PersonMentionHoverCard name={collab.name} person={personObj} />:
                                                            </div>
                                                            <ul className="space-y-1 pl-3 border-l-2 border-slate-200 dark:border-slate-700">
                                                                {(collab.items || []).map((item, i) => (
                                                                    <li key={i} className="text-xs text-slate-600 dark:text-slate-400">
                                                                        <FormattedModalText text={item} allCandidates={allCandidates} />
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    );
                                                })}
                                                {(!expanded.collaboratorActivity || expanded.collaboratorActivity.length === 0) && (
                                                    <p className="text-xs text-slate-400 italic">No external collaborator updates recorded.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Progress Narrative */}
                                {(activeSection === 'all' || activeSection === 'governance') && expanded.recentProgress && expanded.recentProgress.length > 0 && (
                                    <div className="p-4 rounded-xl bg-white dark:bg-[#161f30] border border-slate-200 dark:border-slate-800 space-y-3">
                                        <div className="flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[#03488a] dark:text-[#a7c8ff] text-base">
                                                timeline
                                            </span>
                                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                                Progress Narrative & Revision History
                                            </h4>
                                        </div>
                                        <ul className="space-y-2 relative pl-4 border-l-2 border-blue-200 dark:border-blue-900/60 ml-2">
                                            {expanded.recentProgress.map((prog, idx) => (
                                                <li key={idx} className="text-xs text-slate-700 dark:text-slate-300 relative">
                                                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-[#03488a] dark:bg-[#a7c8ff]" />
                                                    <span>{prog}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Fallback to HTML if summaryData is not present */}
                    {!loading && !summaryData && summaryHtml && (
                        <div
                            className="prose dark:prose-invert max-w-none text-xs leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: summaryHtml }}
                        />
                    )}
                </div>

                {/* ── Footer ──────────────────────────────────────────────── */}
                <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-[#161f30] flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span className="material-symbols-outlined text-sm text-purple-600">bolt</span>
                        <span>Context Engine · Continuous Task Intelligence</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="space-y-4 py-8 animate-pulse">
            <div className="h-20 bg-slate-200 dark:bg-slate-800 rounded-xl w-full" />
            <div className="grid grid-cols-2 gap-4">
                <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded-xl" />
                <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded-xl" />
            </div>
            <div className="h-24 bg-slate-200 dark:bg-slate-800 rounded-xl w-full" />
            <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-2 pt-2">
                <span className="material-symbols-outlined text-purple-600 animate-spin text-sm">
                    progress_activity
                </span>
                Synthesizing multi-source task intelligence…
            </p>
        </div>
    );
}

function ErrorState({ message, onRetry }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950/60 flex items-center justify-center text-rose-600">
                <span className="material-symbols-outlined text-2xl">error</span>
            </div>
            <p className="text-sm font-bold text-slate-800 dark:text-slate-200">Unable to generate summary</p>
            <p className="text-xs text-slate-500 max-w-sm">{message}</p>
            <button
                onClick={onRetry}
                className="mt-2 px-4 py-1.5 bg-[#03488a] text-white text-xs font-bold rounded-lg shadow-sm hover:opacity-90 transition-opacity cursor-pointer"
            >
                Try Again
            </button>
        </div>
    );
}
